import { supabase } from '@/lib/supabase';
import { callFunction } from '@/lib/ensureSession';
import { BOT_USERNAME } from '@/utils/cardCanvas';

// ════════════════════════════════════════════════════════════════
// Отправка карточки другу — одна для всех мест, где есть «поделиться»
// (достижения, неделя, распознанное блюдо, приглашение, подарок).
//
// Слава 07.10.2026: «нажимаешь отправить в ТГ — уходит просто текст, скучно.
// Везде, где предлагает поделиться, должна формироваться красивая карточка».
//
// Порядок попыток:
//   1. Telegram 8.0+ — готовое сообщение (картинка + подпись + кнопка), человек
//      выбирает чат. Картинку в Telegram загружает НАШ сервер (share-prepare):
//      скачать её с нашего домена Telegram не может, входящие от него режутся.
//   2. Telegram старше — карточка приходит человеку в чат с ботом, оттуда
//      её пересылают (кнопка при пересылке сохраняется).
//   3. Без Telegram (сайт) — системное «Поделиться» с картинкой.
//   4. И только если не вышло ничего — ссылка с текстом, как было раньше.
// ════════════════════════════════════════════════════════════════

export { BOT_USERNAME };

export function tg(): any {
  return (window as any).Telegram?.WebApp;
}

export function canShareToStory(): boolean {
  const w = tg();
  return !!w && typeof w.shareToStory === 'function' && (typeof w.isVersionAtLeast !== 'function' || w.isVersionAtLeast('7.8'));
}

/** Готовые сообщения (фото + подпись + кнопка) появились в Telegram 8.0. */
function canShareMessage(): boolean {
  const w = tg();
  return !!w && typeof w.shareMessage === 'function' && (typeof w.isVersionAtLeast !== 'function' || w.isVersionAtLeast('8.0'));
}

/** Мини-приложение открыто внутри Telegram, а не на сайте */
function insideTelegram(): boolean {
  return !!tg()?.initData;
}

/** Ссылка, по которой друг попадает в мини-приложение с реф-кодом */
export function inviteLink(refCode?: string | null): string {
  return refCode ? `https://t.me/${BOT_USERNAME}?startapp=ref_${refCode}` : `https://t.me/${BOT_USERNAME}`;
}

/** Карточки рисуются в PNG; для Telegram ужимаем в JPEG — меньше и быстрее грузится. */
async function toJpeg(png: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(png);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d unavailable');
  ctx.drawImage(bitmap, 0, 0);
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('jpeg encode failed'))), 'image/jpeg', 0.9),
  );
}

/** Загружает карточку в публичную корзину — истории Telegram принимают только URL */
export async function uploadCard(
  blob: Blob,
  userId: string,
  key: string,
  ext: 'png' | 'jpg' = 'png',
): Promise<string> {
  const path = `${userId}/${key.replace(/[^A-Za-z0-9_-]/g, '')}-${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from('share-cards').upload(path, blob, {
    contentType: ext === 'jpg' ? 'image/jpeg' : 'image/png',
    upsert: true,
  });
  if (error) throw error;
  const { data } = supabase.storage.from('share-cards').getPublicUrl(path);
  return data.publicUrl;
}

async function prepare(photoUrl: string, opts: SendOpts, mode: 'prepared' | 'bot_chat'): Promise<any> {
  const res = await callFunction('share-prepare', {
    photo_url: photoUrl,
    caption: opts.caption,
    link: opts.link,
    button: opts.button,
    mode,
  });
  return res.json();
}

type SendOpts = {
  userId: string;
  /** Короткое имя для файла: ration_week, recognition, invite… */
  key: string;
  caption: string;
  /** Только ссылка на нашего бота — сервер другие не примет */
  link: string;
  /** Подпись кнопки под карточкой */
  button?: string;
};

/** Отправить карточку другу в Telegram. false — не вышло, вызывающий решает, что дальше. */
export async function sendCardToFriend(blob: Blob, opts: SendOpts): Promise<boolean> {
  if (!insideTelegram()) return false;
  let photoUrl: string;
  try {
    photoUrl = await uploadCard(await toJpeg(blob), opts.userId, opts.key, 'jpg');
  } catch (e) {
    console.warn('[share] card upload failed:', e);
    return false;
  }

  if (canShareMessage()) {
    try {
      const data = await prepare(photoUrl, opts, 'prepared');
      if (data?.id) {
        tg().shareMessage(data.id);
        return true;
      }
      console.warn('[share] prepared message refused:', data?.error, data?.description);
    } catch (e) {
      console.warn('[share] prepared message failed:', e);
    }
  }

  // Старый Telegram или сбой подготовки — карточка в чат с ботом, оттуда переслать
  try {
    const data = await prepare(photoUrl, opts, 'bot_chat');
    if (data?.sent) {
      const msg = 'Карточка пришла тебе в чат с ботом — перешли её другу 📤';
      const w = tg();
      if (typeof w?.showAlert === 'function') w.showAlert(msg); else alert(msg);
      return true;
    }
    console.warn('[share] bot chat refused:', data?.error, data?.description);
  } catch (e) {
    console.warn('[share] bot chat failed:', e);
  }
  return false;
}

/** Системное «Поделиться» картинкой, иначе скачивание. true — картинка ушла. */
export async function shareImageFile(blob: Blob, fileName: string, text?: string): Promise<boolean> {
  const file = new File([blob], fileName, { type: blob.type || 'image/png' });
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share(text ? { files: [file], text } : { files: [file] });
      return true;
    } catch {
      return true; // отмена человеком — не ошибка, ссылку следом не открываем
    }
  }
  return false;
}

export function downloadImage(blob: Blob, fileName: string): void {
  const u = URL.createObjectURL(blob);
  const el = document.createElement('a');
  el.href = u; el.download = fileName; el.click();
  setTimeout(() => URL.revokeObjectURL(u), 10000);
}

/** Последний запасной путь — ссылка с текстом */
export function shareLinkText(link: string, text: string): void {
  const url = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`;
  const w = tg();
  if (w?.openTelegramLink) w.openTelegramLink(url); else window.open(url, '_blank');
}

/** «Другу» целиком: карточкой в Telegram → картинкой через систему → ссылкой. */
export async function shareCardWithFriend(blob: Blob, opts: SendOpts & { fileName: string; linkText?: string }): Promise<void> {
  if (await sendCardToFriend(blob, opts)) return;
  if (await shareImageFile(blob, opts.fileName, `${opts.caption} ${opts.link}`)) return;
  shareLinkText(opts.link, opts.linkText ?? opts.caption);
}
